import { parseIntermediate } from '../columns';

/**
 * Reading another logbook's idea of a route into this one's.
 *
 * CAA 1373 asks for three things — where the flight started, where it landed
 * in between, where it finished — and the formats this app imports give one
 * or two. ForeFlight has From and To columns plus a Route of what happened in
 * between; MyFlightbook has a single Route string and nothing else. Both have
 * to arrive as `LogbookEntry`'s ordered `intermediate` list, and the guessing
 * belongs here rather than in each format's parser.
 *
 * Nothing here ever refuses a row. A route cell that is not a list of
 * aerodrome codes — a place name from an app that allows them — is dropped,
 * because the rest of the row is a real flight and losing a stop is a much
 * smaller loss than losing the entry.
 */

/** A Route column that holds only the stops in between — ForeFlight's. */
export function intermediateFrom(raw: string): { intermediate?: string[] } {
  const legs = parseIntermediate(raw.trim());
  return legs === undefined || legs.length === 0 ? {} : { intermediate: legs };
}

/**
 * A Route column that holds the whole flight — MyFlightbook's `NZAR NZHN NZAR`.
 *
 * First leg is the departure, last is the arrival, and everything between
 * them is an intermediate landing. A one-leg route is a departure with no
 * arrival recorded, which is what the source file actually said; inventing a
 * return to the same place would be this app deciding where the aeroplane
 * ended up.
 */
export function splitRoute(raw: string): {
  from?: string;
  to?: string;
  intermediate?: string[];
} {
  const legs = parseIntermediate(raw.trim());
  if (legs === undefined || legs.length === 0) {
    return {};
  }
  const [first, ...rest] = legs;
  const last = rest.pop();
  return {
    ...(first === undefined ? {} : { from: first }),
    ...(last === undefined ? {} : { to: last }),
    ...(rest.length === 0 ? {} : { intermediate: rest }),
  };
}
